/**
 * Result and Cost per Result (spec §3.3 boxes 3 and 9, §4), with the same
 * arithmetic as LookML's result / result_spend / cost_per_result (ruling R5):
 * Result sums the mapped metric over rows that report it; its cost sums spend
 * over those rows only, so spend with no Result never inflates the cost;
 * Awareness is judged per 1,000. Never summed across objectives: every figure
 * here is for ONE objective. Pure.
 *
 * @module services/overview/objectiveResults
 */
import { TARGET_OBJECTIVES, type TargetObjective } from '@/services/targets/targetRules';
import { metricFor, type AwarenessMetric, type ResultMetric } from '@/services/targets/resultMetric';
import { deltaOf } from '@/services/looker/lookerDelta';
import type { FactRow, ResultState } from './overviewTypes';
import { addNullable, cpcOf, cpmOf, ctrOf, isLowSample, ratioOf, totalsOf } from './overviewMath';
import { metricValueOf } from './reportedMetric';

export interface ResultFigures {
  /** The metric Result counts; null = no Result (outside the three, or the definition was not read). */
  metric: ResultMetric | null;
  result: number | null;
  costPerResult: number | null;
  /** Why `result` is what it is: notReported ≠ none (I2); NULL is never 0 either way. */
  resultState: ResultState;
}

const NO_RESULT: ResultFigures = { metric: null, result: null, costPerResult: null, resultState: 'none' };

export const asTargetObjective = (o: string): TargetObjective | null =>
  ((TARGET_OBJECTIVES as readonly string[]).includes(o) ? (o as TargetObjective) : null);

const scaleOf = (o: TargetObjective): number => (o === 'awareness' ? 1000 : 1);

/** `rows` is already the window Result reads (the cut). awareness null = the mapping read failed (R6). */
export const resultFiguresOf = (rows: readonly FactRow[], objective: string, awareness: AwarenessMetric | null): ResultFigures => {
  const o = asTargetObjective(objective);
  if (o === null || awareness === null) return NO_RESULT;
  const metric = metricFor(o, awareness);
  const reporting = rows.filter((r) => r.objective === o && metricValueOf(r, metric) !== null);
  const result = reporting.reduce<number | null>((s, r) => addNullable(s, metricValueOf(r, metric)), null);
  const spend = reporting.reduce<number | null>((s, r) => addNullable(s, r.spend), null);
  const spent = rows.some((r) => r.objective === o && (r.spend ?? 0) > 0);
  const resultState: ResultState = result !== null ? 'value' : spent ? 'notReported' : 'none';
  return { metric, result, costPerResult: ratioOf(spend, result, scaleOf(o)), resultState };
};

export interface ResultCardFigures {
  objective: TargetObjective;
  metric: ResultMetric | null;
  result: number | null;
  costPerResult: number | null;
  resultState: ResultState;
  resultDelta: number | null;
  costPerResultDelta: number | null;
  /** R17 on the objective's rows of the period, as box 9's row (rules-04 D1): Cost per Result fades; Result never does. */
  lowSample: boolean;
}

/** Box 3, for the one selected objective: the cut window against the cut previous window; n<500 from `current`, as box 9. */
export const resultCardOf = (
  objective: TargetObjective, current: readonly FactRow[], cut: readonly FactRow[], cutPrevious: readonly FactRow[],
  awareness: AwarenessMetric | null,
): ResultCardFigures => {
  const now = resultFiguresOf(cut, objective, awareness);
  const before = resultFiguresOf(cutPrevious, objective, awareness);
  return {
    objective, metric: now.metric, result: now.result, costPerResult: now.costPerResult, resultState: now.resultState,
    resultDelta: deltaOf(now.result, before.result),
    costPerResultDelta: deltaOf(now.costPerResult, before.costPerResult),
    lowSample: isLowSample(totalsOf(current.filter((r) => r.objective === objective))),
  };
};

export interface ObjectiveRow {
  objective: string;
  spend: number | null;
  /** This objective's share of the period's spend (no totals row is ever drawn). */
  share: number | null;
  impressions: number | null;
  clicks: number | null;
  ctr: number | null;
  cpc: number | null;
  cpm: number | null;
  lowSample: boolean;
  metric: ResultMetric | null;
  result: number | null;
  costPerResult: number | null;
  resultState: ResultState;
  costPerResultDelta: number | null;
}

const ORDER: readonly string[] = TARGET_OBJECTIVES;
const rank = (o: string): number => (ORDER.includes(o) ? ORDER.indexOf(o) : ORDER.length);
const byObjective = (a: string, b: string): number => rank(a) - rank(b) || a.localeCompare(b);

/** Box 9: one row per objective present in `current`. Spend columns read `current`; Result columns the cut. */
export const objectiveRowsOf = (
  current: readonly FactRow[], cut: readonly FactRow[], cutPrevious: readonly FactRow[], awareness: AwarenessMetric | null,
): ObjectiveRow[] => {
  const total = totalsOf(current).spend;
  return [...new Set(current.map((r) => r.objective))].sort(byObjective).map((objective): ObjectiveRow => {
    const t = totalsOf(current.filter((r) => r.objective === objective));
    const now = resultFiguresOf(cut, objective, awareness);
    const before = resultFiguresOf(cutPrevious, objective, awareness);
    return {
      objective,
      spend: t.spend,
      share: ratioOf(t.spend, total),
      impressions: t.impressions,
      clicks: t.clicks,
      ctr: ctrOf(t),
      cpc: cpcOf(t),
      cpm: cpmOf(t),
      lowSample: isLowSample(t),
      metric: now.metric,
      result: now.result,
      costPerResult: now.costPerResult,
      resultState: now.resultState,
      costPerResultDelta: deltaOf(now.costPerResult, before.costPerResult),
    };
  });
};
